import { type AppointmentItem, DASHBOARD_CARDS, DASHBOARD_CARD_LABELS, type DashboardCard, type DashboardLayout, type DashboardResponse, type ExternalEvent, type JobListItem, type TaskItem } from "@nmt/shared";
import { InsuranceCard } from "../components/insurance";
import { AssistantCard } from "../components/assistant";
import { PastDueCard } from "../components/past-due";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { del, errorMessage, get, put } from "../api";
import { NewJobDialog } from "../components/forms";
import { ExternalDialog, asItem, isExternal } from "../components/external-events";
import { AppointmentDialog, TaskDialog, TaskRows, toDateInput } from "../components/schedule";
import { Alert, Button, Dialog, Empty, Loading, Panel, PanelHeader, StageBadge, ago, cx } from "../components/ui";
import { useSession } from "../session";

const WIDE = new Set<DashboardCard>(["stats", "assistant", "pastDue", "insurance"]);

/** Someone's cards in their order (cards added since they saved go at the end), minus the hidden ones. */
function layoutOf(saved: DashboardLayout | null): { layout: DashboardCard[]; hidden: DashboardCard[] } {
  const order = [...(saved?.order ?? []).filter((k) => DASHBOARD_CARDS.includes(k)), ...DASHBOARD_CARDS.filter((k) => !saved?.order.includes(k))];
  const hidden = saved?.hidden ?? [];
  return { layout: order.filter((k) => !hidden.includes(k)), hidden };
}

/** Customize: tick cards on or off and move them up or down; saved for this person only. */
function CustomizeDialog({ current, hidden, onClose }: { current: DashboardCard[]; hidden: DashboardCard[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [order, setOrder] = useState<DashboardCard[]>([...current, ...hidden]);
  const [off, setOff] = useState<DashboardCard[]>(hidden);
  const save = useMutation({
    mutationFn: (layout: DashboardLayout | null) => (layout ? put("/api/me/dashboard", layout) : del("/api/me/dashboard")),
    onSuccess: async () => (await qc.invalidateQueries({ queryKey: ["me"] }), onClose()),
  });
  const move = (i: number, by: number) => {
    const j = i + by;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setOrder(next);
  };
  return (
    <Dialog
      title="Customize your dashboard"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" className="mr-auto" busy={save.isPending && save.variables === null} onClick={() => save.mutate(null)}>
            Back to the default
          </Button>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending && save.variables !== null} onClick={() => save.mutate({ order, hidden: off })}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-1">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <p className="mb-2 text-sm text-ink-soft">Just for you. Cards you can't use (for example, money without permission) don't show either way.</p>
        {order.map((k, i) => (
          <div key={k} className="flex items-center gap-2 rounded-[var(--radius-control)] px-2 py-1.5 hover:bg-mist/60">
            <input type="checkbox" checked={!off.includes(k)} onChange={(e) => setOff(e.target.checked ? off.filter((x) => x !== k) : [...off, k])} aria-label={`Show ${DASHBOARD_CARD_LABELS[k]}`} className="size-4 accent-[var(--color-brand)]" />
            <span className={cx("flex-1 text-sm", off.includes(k) && "text-muted line-through")}>{DASHBOARD_CARD_LABELS[k]}</span>
            <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)} className="rounded px-2 py-1 text-muted hover:bg-white hover:text-ink disabled:opacity-30">
              ↑
            </button>
            <button type="button" aria-label="Move down" disabled={i === order.length - 1} onClick={() => move(i, 1)} className="rounded px-2 py-1 text-muted hover:bg-white hover:text-ink disabled:opacity-30">
              ↓
            </button>
          </div>
        ))}
      </div>
    </Dialog>
  );
}

function greeting(tz: string) {
  const hour = Number(new Intl.DateTimeFormat("en-US", { hour: "numeric", hourCycle: "h23", timeZone: tz }).format(new Date()));
  return hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
}

export function Dashboard() {
  const { me, can } = useSession();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const { layout, hidden: hiddenCards } = layoutOf(me?.dashboard ?? null);
  // Half-width cards side by side, two at a time in order; one left on its own goes full width.
  const paired = new Set<DashboardCard>();
  for (let i = 0; i < layout.length - 1; i++) {
    const [a, b] = [layout[i]!, layout[i + 1]!];
    if (!WIDE.has(a) && !WIDE.has(b) && !paired.has(a)) {
      paired.add(a);
      paired.add(b);
      i++;
    }
  }
  const data = useQuery({ queryKey: ["dashboard"], queryFn: () => get<DashboardResponse>("/api/dashboard"), enabled: can("customers.view") });
  if (!me) return null;
  const tz = me.company.timezone;
  const today = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: tz }).format(new Date());

  const openCount = data.data?.pipeline.reduce((n, w) => n + w.stages.reduce((m, s) => m + s.count, 0), 0) ?? 0;
  const leadCount = data.data?.newLeadCount ?? 0;
  const stuckCount = data.data?.stuckCount ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="hero-gradient -mx-4 -mt-6 flex flex-wrap items-center justify-between gap-4 px-6 py-6 text-white sm:mx-0 sm:mt-0 sm:rounded-[var(--radius-panel)]">
        <div>
          <h1 className="text-[22px] font-semibold">
            {greeting(tz)}, {me.user.firstName}
          </h1>
          <p className="mt-0.5 text-sm text-white/75">{today}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button className="border-white/40 bg-white/10 text-white hover:bg-white/20" onClick={() => setCustomizing(true)}>
            Customize
          </Button>
          {can("jobs.edit") && (
            <Button variant="primary" onClick={() => setAdding(true)}>
              + New job
            </Button>
          )}
        </div>
      </div>

      {data.isLoading ? (
        <Loading />
      ) : data.error ? (
        <Alert>{errorMessage(data.error)}</Alert>
      ) : data.data ? (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-2">
          {layout.map((k) => {
            const d = data.data!;
            const card: Record<DashboardCard, { wide?: boolean; node: React.ReactNode }> = {
              stats: {
                wide: true,
                node: (
                  <div className="grid gap-4 sm:grid-cols-3">
                    <Stat label="Open jobs" value={openCount} sub="Across all job types" onClick={() => navigate("/jobs")} />
                    <Stat label="New leads" value={leadCount} sub="In each workflow's first stage" onClick={() => navigate(NEW_LEADS_URL)} />
                    <Stat label="Need a nudge" value={stuckCount} sub="Same stage for 14+ days" onClick={() => navigate(NUDGE_URL)} />
                  </div>
                ),
              },
              assistant: { wide: true, node: <AssistantCard /> },
              tasks: { node: <MyTasks /> },
              agenda: { node: <Agenda /> },
              pastDue: { wide: true, node: <PastDueCard /> },
              insurance: { wide: true, node: <InsuranceCard /> },
              pipeline: {
                node: (
                  <Panel>
                    <PanelHeader title="Pipeline" description="Open jobs by stage. Click a stage to see its jobs." />
                    <div className="flex flex-col gap-5 px-5 py-4">
                      {d.pipeline.map((w) => (
                        <div key={w.workflowId}>
                          <div className="mb-2 text-sm font-semibold text-ink-soft">{w.workflowName}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {w.stages.map((s) => (
                              <Link
                                key={s.id}
                                to={`/jobs?workflowId=${w.workflowId}&stageId=${s.id}`}
                                className={`rounded-full px-3 py-1 text-sm font-medium ${s.count ? "bg-brand-wash text-brand-deep hover:bg-brand hover:text-white" : "bg-mist text-muted hover:text-ink"}`}
                              >
                                {s.name} <span className="font-bold">{s.count}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Panel>
                ),
              },
              attention: {
                node: (
                  <Panel>
                    <PanelHeader title="Needs attention" action={<SeeAll to={NUDGE_URL} />} />
                    {!d.stuckJobs.length ? <Empty>Nothing stuck. Nice.</Empty> : <JobRows jobs={d.stuckJobs} note={(j) => `${j.stageName} since ${ago(j.stageChangedAt)}`} />}
                  </Panel>
                ),
              },
              newLeads: {
                node: (
                  <Panel>
                    <PanelHeader title="New leads" action={<SeeAll to={NEW_LEADS_URL} />} />
                    {!d.newLeads.length ? <Empty>No new leads.</Empty> : <JobRows jobs={d.newLeads} note={(j) => [j.leadSource, ago(j.createdAt)].filter(Boolean).join(" · ")} />}
                  </Panel>
                ),
              },
              recent: {
                node: (
                  <Panel>
                    <PanelHeader title="Recently updated" />
                    {!d.recentJobs.length ? <Empty>No jobs yet. Add a customer to get started.</Empty> : <JobRows jobs={d.recentJobs} note={(j) => `Updated ${ago(j.updatedAt)}`} />}
                  </Panel>
                ),
              },
            };
            const c = card[k];
            return (
              <div key={k} className={cx("min-w-0 empty:hidden", (c.wide || !paired.has(k)) && "lg:col-span-2")}>
                {c.node}
              </div>
            );
          })}
        </div>
      ) : null}

      {customizing && <CustomizeDialog current={layout} hidden={hiddenCards} onClose={() => setCustomizing(false)} />}
      {adding && <NewJobDialog onClose={() => setAdding(false)} />}
    </div>
  );
}

/** The Jobs page with what the cards count: each type's first stage, and 14+ days in one stage (open jobs). */
const NEW_LEADS_URL = "/jobs?firstStage=1&sort=newest&columns=address,stage,rep,leadSource,created";
const NUDGE_URL = "/jobs?stageDaysMin=14&sort=stageAge&columns=address,stage,rep,stageDays";

function SeeAll({ to }: { to: string }) {
  return (
    <Link to={to} className="text-sm font-semibold text-brand hover:underline">
      See all
    </Link>
  );
}

function Stat({ label, value, sub, onClick }: { label: string; value: number; sub: string; onClick?: () => void }) {
  const body = (
    <>
      <div className="text-xs font-medium tracking-wide text-muted uppercase">{label}</div>
      <div className="mt-1.5 text-[28px] leading-none font-bold">{value}</div>
      <div className="mt-1.5 text-xs text-muted">{sub}</div>
    </>
  );
  const cls = "rounded-[var(--radius-panel)] border-t-4 border-brand bg-white p-[18px] text-left shadow-[var(--shadow-card)]";
  return onClick ? (
    <button className={`${cls} hover:shadow-md`} onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
}

function JobRows({ jobs, note }: { jobs: JobListItem[]; note: (j: JobListItem) => string }) {
  return (
    <ul>
      {jobs.map((j) => (
        <li key={j.id} className="border-b border-mist last:border-0">
          <Link to={`/jobs/${j.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-mist/50">
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">
                #{j.number} {j.customerName}
                {j.title && <span className="font-normal text-ink-soft"> — {j.title}</span>}
              </div>
              <div className="truncate text-xs text-muted">
                {[j.workflowName, j.city, note(j)].filter(Boolean).join(" · ")}
              </div>
            </div>
            <StageBadge name={j.stageName} kind={j.stageKind} />
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** The signed-in person's open tasks: High, Medium, Low, then by due date; overdue in red. */
function MyTasks() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const tasks = useQuery({ queryKey: ["tasks", "mine"], queryFn: () => get<TaskItem[]>("/api/tasks?limit=15") });
  const refresh = () => qc.invalidateQueries({ queryKey: ["tasks"] });
  const overdue = tasks.data?.filter((t) => t.dueAt && new Date(t.dueAt) < new Date()).length ?? 0;
  return (
    <Panel>
      <PanelHeader
        title="My tasks"
        description={overdue ? `${overdue} overdue` : undefined}
        action={
          <div className="flex gap-2">
            <Link to="/tasks" className="self-center text-sm font-medium text-muted hover:text-ink">
              All tasks
            </Link>
            <Button className="px-3 py-1.5" onClick={() => setAdding(true)}>
              Add
            </Button>
          </div>
        }
      />
      {tasks.isLoading ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : (
        <TaskRows tasks={tasks.data ?? []} empty="Nothing to do. Nice." onChanged={refresh} />
      )}
      {adding && <TaskDialog onClose={() => setAdding(false)} onSaved={refresh} />}
    </Panel>
  );
}

const AGENDA_DAYS = 14;
const PHONE_DAYS = 4;
const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** Wide enough that the agenda sits beside My tasks (and fits its height). */
function useWide() {
  const query = "(min-width: 1024px)";
  const [wide, setWide] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setWide(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return wide;
}

/**
 * My agenda: today (even when empty), then the next days with something on them, from the CRM and my
 * other calendars. Beside My tasks it shows the whole days that fit its height; on a phone, a few days.
 */
function Agenda() {
  const { me } = useSession();
  const qc = useQueryClient();
  const wide = useWide();
  const [open, setOpen] = useState<AppointmentItem | null>(null);
  const [openExt, setOpenExt] = useState<ExternalEvent | null>(null);
  const today = dayStart(new Date());
  const end = addDays(today, AGENDA_DAYS);
  const qs = new URLSearchParams({ from: today.toISOString(), to: end.toISOString(), users: me?.user.id ?? "" });
  const list = useQuery({ queryKey: ["appointments", "agenda", qs.toString()], queryFn: () => get<AppointmentItem[]>(`/api/appointments?${qs}`), enabled: !!me });
  const ext = useQuery({ queryKey: ["external-events", "agenda", qs.toString()], queryFn: () => get<ExternalEvent[]>(`/api/calendar/external?${qs}`), enabled: !!me, staleTime: 60_000 });

  const days = useMemo(() => {
    const items = [...(list.data ?? []), ...(ext.data ?? []).filter((e) => e.mine).map(asItem)];
    const out: { day: Date; items: AppointmentItem[] }[] = [];
    for (let i = 0; i < AGENDA_DAYS; i++) {
      const day = addDays(today, i);
      const on = items
        .filter((a) => dayStart(new Date(a.startsAt)) <= day && day <= dayStart(new Date(a.allDay ? a.endsAt : new Date(new Date(a.endsAt).getTime() - 1))))
        .sort((x, y) => Number(y.allDay) - Number(x.allDay) || x.startsAt.localeCompare(y.startsAt));
      if (i === 0 || on.length) out.push({ day, items: on });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, ext.data, today.getTime()]);

  // Beside My tasks: the agenda takes the row's height and shows the whole days that fit.
  const box = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(days.length);
  useLayoutEffect(() => {
    const el = box.current;
    if (!wide || !el) return;
    const measure = () => {
      const groups = [...el.querySelectorAll<HTMLElement>("[data-day]")];
      let n = 0;
      for (const g of groups) {
        if (g.offsetTop + g.offsetHeight > el.clientHeight + 1) break;
        n++;
      }
      setFit(Math.max(1, n));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [wide, days]);
  const shown = wide ? days.slice(0, fit) : days.slice(0, PHONE_DAYS);
  const next = days[shown.length];
  const more = next ? `/calendar?date=${toDateInput(next.day)}` : `/calendar?date=${toDateInput(end)}`;

  const time = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const label = (d: Date) => {
    const i = Math.round((d.getTime() - today.getTime()) / 86_400_000);
    const date = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    return i === 0 ? `Today · ${date}` : i === 1 ? `Tomorrow · ${date}` : date;
  };
  const row = (a: AppointmentItem) => {
    const external = isExternal(a);
    return (
      <li key={`${a.id}`} className="border-b border-mist last:border-0">
        <button
          className="flex w-full gap-3 px-5 py-2.5 text-left hover:bg-mist/50"
          onClick={() => (external ? setOpenExt(ext.data?.find((x) => `ext:${x.id}` === a.id) ?? null) : setOpen(a))}
        >
          <span className="w-16 shrink-0 text-sm text-muted">{a.allDay ? "All day" : time(a.startsAt)}</span>
          <span className="min-w-0">
            <span className={cx("block truncate font-semibold", external && "font-medium text-ink-soft italic")}>
              {external && <span className="mr-1.5 inline-block size-2 rounded-full border-2 align-middle" style={{ borderColor: a.attendees[0]?.color }} />}
              {a.title}
            </span>
            <span className="block truncate text-xs text-muted">{(external ? [a.location, a.attendees[0]?.name] : [a.contactName, a.location]).filter(Boolean).join(" · ")}</span>
          </span>
        </button>
      </li>
    );
  };

  return (
    <Panel className="flex flex-col lg:min-h-[24rem]">
      <PanelHeader
        title="Agenda"
        action={
          <Link to="/calendar" className="text-sm font-medium text-muted hover:text-ink">
            Calendar
          </Link>
        }
      />
      {list.isLoading ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : (
        <>
          <div className="lg:relative lg:min-h-0 lg:flex-1">
            <div ref={box} className={cx("lg:absolute lg:inset-0", wide && fit <= 1 ? "overflow-y-auto" : "overflow-hidden")}>
              {(wide ? days : shown).map((g, i) => (
                <section key={g.day.getTime()} data-day className={cx(wide && i >= fit && "invisible")}>
                  <h3 className="bg-mist/60 px-5 py-1.5 text-xs font-semibold tracking-wide text-ink-soft uppercase">{label(g.day)}</h3>
                  {g.items.length ? <ul>{g.items.map(row)}</ul> : <div className="px-5 py-3 text-sm text-muted">Nothing today.</div>}
                </section>
              ))}
            </div>
          </div>
          <Link to={more} className="border-t border-mist px-5 py-2.5 text-sm font-medium text-brand hover:underline">
            {next ? `See more on the calendar (${label(next.day).replace(/^Tomorrow · /, "tomorrow, ")})` : "See more on the calendar"}
          </Link>
        </>
      )}
      {open && <AppointmentDialog appointment={open} onClose={() => setOpen(null)} onSaved={() => qc.invalidateQueries({ queryKey: ["appointments"] })} />}
      {openExt && <ExternalDialog e={openExt} onClose={() => setOpenExt(null)} />}
    </Panel>
  );
}
